/**
 * A Strat screen's own controls (`docs/plans/strategy-lists.md`, phase 5).
 *
 *     ◂  Oct 2026  ▸   [confirm]                       — the screener bar's settings slot
 *     History | Why ▾   — below the grid: 12 months of weights and returns, or
 *                          what decided the month in the stepper (owner, 2026-10-27)
 *
 * Shown only while exactly one Strat chip is the screen (`lib/strategy.ts`).
 * The month is what the server is asked for; `undefined` means the newest, so a
 * screen left on it follows the next build rather than pinning a month.
 */

import { useState } from "react";

import type { StrategyHistory, StrategyWhy } from "../api/symbols";
import { whyView } from "../lib/strategy-why";
import { heldMonth, monthLabel, statusTone } from "../lib/strategy";

export interface StrategyStepperProps {
  /** The month on screen, `YYYY-MM`; null while the first answer is on its way. */
  month: string | null;
  status: string | null;
  canBack: boolean;
  canForward: boolean;
  onStep: (by: -1 | 1) => void;
}

export function StrategyStepper({ month, status, canBack, canForward, onStep }: StrategyStepperProps) {
  return (
    <div style={INLINE} role="toolbar" aria-label="Strategy month">
      <span style={SEG}>
        <button type="button" style={STEP} disabled={!canBack} onClick={() => onStep(-1)} aria-label="Previous month">
          ◂
        </button>
        <span style={MONTH}>{month ? monthLabel(month) : "…"}</span>
        <button type="button" style={STEP} disabled={!canForward} onClick={() => onStep(1)} aria-label="Next month">
          ▸
        </button>
      </span>
      {status && (
        <span style={{ ...BADGE, ...TONE[statusTone(status, month ?? undefined, heldMonth())] }} title="Where this month's answer stands">
          {status}
        </span>
      )}
    </div>
  );
}

export interface StrategyHistoryStripProps {
  history: StrategyHistory | null;
  /** Under the touch shell's bars, where it needs its own cap; in the scroll area it does not. */
  capped?: boolean;
  /** The stepper month's Why, or null while it loads / where there is none. */
  why: StrategyWhy | null;
  /** The month on screen, highlighted; a click on another steps to it. */
  month: string | null;
  onPick: (month: string) => void;
}

export function StrategyHistoryStrip({ history, why, month, onPick, capped = false }: StrategyHistoryStripProps) {
  const [open, setOpen] = useState(true);
  const [tab, setTab] = useState<"history" | "why">("history");
  const months = history?.months ?? [];
  const on = (m: string) => month !== null && m.slice(0, 7) === month.slice(0, 7);
  const tabButton = (t: "history" | "why", label: string) => (
    <button
      type="button"
      role="tab"
      aria-selected={tab === t}
      style={{ ...TOGGLE, ...(tab === t ? { color: "var(--qf-text)", fontWeight: 600 } : {}) }}
      onClick={() => {
        setTab(t);
        setOpen(true);
      }}
    >
      {label}
    </button>
  );
  return (
    <div style={capped ? { ...STRIP, maxHeight: "40%", overflowY: "auto" } : STRIP}>
      <div role="tablist" style={{ display: "flex", alignItems: "center" }}>
        {tabButton("history", "History")}
        <span style={{ color: "var(--qf-border)" }}>|</span>
        {tabButton("why", "Why")}
        <button type="button" style={TOGGLE} onClick={() => setOpen((v) => !v)} aria-expanded={open} aria-label={open ? "Collapse" : "Expand"}>
          {open ? "▾" : "▸"}
        </button>
      </div>
      {open && tab === "why" && <WhyPanel why={why} />}
      {open && tab === "history" && months.length > 0 && (
        <div style={{ overflowX: "auto" }}>
          <table style={TABLE}>
            <thead>
              <tr>
                <th style={HEAD_LEFT} />
                {months.map((m) => (
                  <th key={m} style={{ ...HEAD, ...(on(m) ? CURRENT : {}) }}>
                    <button type="button" style={PICK} onClick={() => onPick(m.slice(0, 7))}>
                      {monthLabel(m).replace(" 20", " ’")}
                    </button>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {history!.funds.map((fund) => (
                <tr key={fund.symbol}>
                  <td style={LABEL}>{fund.symbol}</td>
                  {fund.weights.map((w, i) => (
                    <td key={months[i]} style={{ ...CELL, ...(on(months[i]!) ? CURRENT : {}), ...(w ? {} : MUTED) }}>
                      {w ? w.toFixed(0) : "·"}
                    </td>
                  ))}
                </tr>
              ))}
              <tr>
                <td style={{ ...LABEL, borderTop: "1px solid var(--qf-border)" }}>Return %</td>
                {history!.returns.map((r, i) => (
                  <td
                    key={months[i]}
                    style={{
                      ...CELL,
                      borderTop: "1px solid var(--qf-border)",
                      ...(on(months[i]!) ? CURRENT : {}),
                      color: r == null ? "var(--qf-text-muted)" : r >= 0 ? "var(--qf-positive)" : "var(--qf-negative)",
                    }}
                    title={history!.proxy[i] ? "Priced on a stand-in" : undefined}
                  >
                    {r == null ? "·" : `${(r * 100).toFixed(1)}${history!.proxy[i] ? "*" : ""}`}
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function WhyPanel({ why }: { why: StrategyWhy | null }) {
  if (!why) return <div style={{ ...LABEL, padding: "4px 8px", color: "var(--qf-text-muted)" }}>Nothing for this month yet.</div>;
  const view = whyView(why);
  const preview = why.status === "preview";
  return (
    <div style={{ padding: "2px 8px 8px", fontSize: 12 }}>
      <div style={{ color: "var(--qf-text-secondary)", marginBottom: 6 }}>
        {monthLabel(why.month)} · {why.status} · decided {why.decidedOn}
      </div>
      <table style={TABLE}>
        <tbody>
          {view.parts.map((part) => (
            <tr key={part.label} style={{ verticalAlign: "top" }}>
              <td style={{ ...LABEL, fontWeight: 600, paddingLeft: 0 }}>{part.label}</td>
              <td style={{ padding: "1px 8px" }}>
                <div>
                  {part.readings.map((r) => (
                    <span key={r.label} style={ITEM}>
                      <span style={MUTED}>{r.label}</span> {r.value}
                    </span>
                  ))}
                </div>
                {part.margins.length > 0 && (
                  <div title="How far each rule is from flipping the other way">
                    <span style={{ ...MUTED, marginRight: 8 }}>Margin</span>
                    {part.margins.map((m) => (
                      <span key={m.label} style={{ ...ITEM, ...(m.close ? { color: "var(--qf-warn)" } : {}) }}>
                        <span style={m.close ? undefined : MUTED}>{m.label}</span> {m.value}
                        {m.close && " ⚠ close"}
                      </span>
                    ))}
                  </div>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {/* A preview has no later run to differ from — said only once the month-end runs exist. */}
      {!preview && view.changed.length > 0 && (
        <div style={{ marginTop: 6 }} title="Whether a later month-end run gave a different answer">
          <span style={{ ...MUTED, marginRight: 8 }}>Changed</span>
          {view.changed.map((c) => (
            <span key={c.label} style={{ ...ITEM, ...(c.changed ? { color: "var(--qf-warn)" } : {}) }}>
              {c.label}: {c.changed ? "yes" : "no"}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

const ITEM: React.CSSProperties = { marginRight: 14, whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" };
const INLINE: React.CSSProperties = { display: "flex", alignItems: "center", gap: 8, flex: "0 0 auto" };
const SEG: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  border: "1px solid var(--qf-border)",
  borderRadius: 5,
  overflow: "hidden",
};
const STEP: React.CSSProperties = {
  border: 0,
  padding: "3px 8px",
  font: "inherit",
  fontSize: 12,
  cursor: "pointer",
  background: "transparent",
  color: "var(--qf-text)",
};
const MONTH: React.CSSProperties = { fontSize: 12, padding: "0 6px", minWidth: 64, textAlign: "center" };
const BADGE: React.CSSProperties = { fontSize: 11, padding: "1px 7px", borderRadius: 9, border: "1px solid" };
const TONE: Record<"final" | "live" | "preview", React.CSSProperties> = {
  final: { color: "var(--qf-text-secondary)", borderColor: "var(--qf-border)" },
  live: { color: "var(--qf-accent)", borderColor: "var(--qf-accent)" },
  preview: { color: "var(--qf-warn)", borderColor: "var(--qf-warn)" },
};
const STRIP: React.CSSProperties = {
  flex: "0 0 auto",
  borderTop: "1px solid var(--qf-border)",
  background: "var(--qf-surface)",
};
const TOGGLE: React.CSSProperties = {
  border: 0,
  background: "transparent",
  color: "var(--qf-text-secondary)",
  font: "inherit",
  fontSize: 12,
  padding: "3px 8px",
  cursor: "pointer",
};
const TABLE: React.CSSProperties = { borderCollapse: "collapse", fontSize: 11, fontVariantNumeric: "tabular-nums" };
const HEAD: React.CSSProperties = { padding: 0, fontWeight: 400 };
const HEAD_LEFT: React.CSSProperties = { ...HEAD, minWidth: 64 };
const PICK: React.CSSProperties = {
  border: 0,
  background: "transparent",
  color: "var(--qf-text-secondary)",
  font: "inherit",
  padding: "2px 6px",
  cursor: "pointer",
};
const LABEL: React.CSSProperties = { padding: "1px 8px", color: "var(--qf-text)", whiteSpace: "nowrap" };
const CELL: React.CSSProperties = { padding: "1px 6px", textAlign: "right", minWidth: 40 };
const CURRENT: React.CSSProperties = { background: "var(--qf-selection)" };
const MUTED: React.CSSProperties = { color: "var(--qf-text-muted)" };
